// Parser del formato RockSim `.rse` (XML). Ver docs/theory/motors.md.
import { XMLParser } from 'fast-xml-parser';
import { MAX_MOTOR_FILE_BYTES, type MotorParseError, type MotorParseResult } from './eng';
import { createMotor, type Motor } from './motor';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const asArray = (v: unknown): unknown[] => (v === undefined ? [] : Array.isArray(v) ? v : [v]);
const num = (v: unknown): number => (typeof v === 'string' && v.trim() !== '' ? Number(v) : typeof v === 'number' ? v : Number.NaN);

export function parseRse(text: string): MotorParseResult {
  const motors: Motor[] = [];
  const errors: MotorParseError[] = [];
  if (text.length > MAX_MOTOR_FILE_BYTES) return { motors, errors: [{ message: 'El archivo supera el tamaño máximo permitido' }] };

  let doc: unknown;
  try {
    doc = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: '',
      parseAttributeValue: false,
      parseTagValue: false,
      processEntities: false,
    }).parse(text);
  } catch {
    return { motors, errors: [{ message: 'El archivo no es XML válido' }] };
  }

  const db = isObj(doc) ? doc['engine-database'] : undefined;
  const list = isObj(db) ? db['engine-list'] : undefined;
  if (!isObj(list)) return { motors, errors: [{ message: 'No se encontró <engine-list> en el archivo' }] };

  for (const raw of asArray(list['engine'])) {
    if (!isObj(raw)) continue;
    const code = typeof raw['code'] === 'string' ? raw['code'] : '';
    const data = isObj(raw['data']) ? raw['data'] : undefined;
    const points = asArray(data?.['eng-data']).map((p): [number, number] => (isObj(p) ? [num(p['t']), num(p['f'])] : [Number.NaN, Number.NaN]));

    const delayToken = typeof raw['delays'] === 'string' ? raw['delays'] : '';
    const delays: number[] = [];
    let plugged = false;
    let delaysOk = true;
    for (const part of delayToken.split(',').map((s) => s.trim()).filter((s) => s !== '')) {
      if (/^p$/i.test(part)) plugged = true;
      else if (Number.isFinite(num(part)) && num(part) >= 0) delays.push(num(part));
      else delaysOk = false;
    }
    if (!delaysOk) {
      errors.push({ motor: code, message: `Retardos inválidos: ${delayToken}` });
      continue;
    }

    const exit = num(raw['exitDia']);
    const r = createMotor({
      designation: code,
      manufacturer: typeof raw['mfg'] === 'string' && raw['mfg'] !== '' ? raw['mfg'] : 'Desconocido',
      diameter: num(raw['dia']) / 1000,
      length: num(raw['len']) / 1000,
      delays,
      plugged,
      propellantMass: num(raw['propWt']) / 1000,
      totalMass: num(raw['initWt']) / 1000,
      ...(Number.isFinite(exit) && exit > 0 ? { nozzleExitDiameter: exit / 1000 } : {}),
      points,
    });
    if (r.ok) motors.push(r.motor);
    else errors.push({ motor: code, message: r.error });
  }
  return { motors, errors };
}
